import { Compass, Handshake, Building2, CalendarDays, CircleDot, MessageSquare, User } from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import { selection } from '@/platform/haptics'

// Kept for back-compat with AppShell prop typing — role no longer changes the
// tab set since the mobile design ships a single 5-tab bar for everyone.
export type BottomTabRole = 'member' | 'organizer' | 'admin'

interface TabItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string; strokeWidth?: number | string }>
  /** Key into the optional `badges` map for a numeric notification dot. */
  badgeKey?: string
}

export interface BottomTabBarProps {
  role: BottomTabRole
  currentPath: string
  onNavigate: (href: string) => void
  /** Per-tab unread counters keyed by `badgeKey`. */
  badges?: Record<string, number>
  /**
   * When true, surface the Relationships tab. The Discover page only registers
   * the `relationships` sub-tab for Circle Managers / Mafao employees — hiding
   * the bottom tab here keeps the bar in sync so the link never falls back to
   * Associations content.
   */
  isCircleManager?: boolean
}

// Mobile design source of truth: the Discover redesign in product/sections/.
// Relationships is its own dedicated page (BR dashboard + list + create
// wizard) surfaced only for Circle Managers. Events routes to the role-aware
// EventsPage (create/manage for organizers & admins, browse + RSVP for members).
const TABS: TabItem[] = [
  { label: 'Discover',      href: ROUTES.DISCOVER,        icon: Compass },
  { label: 'Relationships', href: ROUTES.RELATIONSHIPS,   icon: Handshake },
  { label: 'Associations',  href: ROUTES.ASSOCIATIONS,    icon: Building2, badgeKey: 'associations' },
  { label: 'Events',        href: ROUTES.EVENTS,          icon: CalendarDays, badgeKey: 'events' },
  { label: 'Circles',       href: ROUTES.CIRCLES,         icon: CircleDot, badgeKey: 'circles' },
  { label: 'Messages',      href: ROUTES.MESSAGES,        icon: MessageSquare, badgeKey: 'messages' },
  { label: 'Profile',       href: ROUTES.PROFILE,         icon: User },
]

function isActive(currentPath: string, href: string): boolean {
  const [hrefPath] = href.split('?')
  const [path] = currentPath.split('?')
  return path === hrefPath || path.startsWith(hrefPath + '/')
}

export function BottomTabBar({ currentPath, onNavigate, badges, isCircleManager }: BottomTabBarProps) {
  const visibleTabs = isCircleManager
    ? TABS
    : TABS.filter((tab) => tab.href !== ROUTES.RELATIONSHIPS)
  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 z-40 w-screen max-w-[100vw] overflow-hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-slate-200/60 dark:border-slate-800/60 safe-area-bottom"
      aria-label="Bottom navigation"
    >
      <div className="flex items-stretch h-16 w-full box-border">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon
          const active = isActive(currentPath, tab.href)
          const badgeCount = tab.badgeKey ? (badges?.[tab.badgeKey] ?? 0) : 0
          return (
            <button
              key={tab.label}
              onClick={() => {
                void selection()
                onNavigate(tab.href)
              }}
              className={`relative flex flex-col items-center justify-center flex-1 basis-0 min-w-0 px-0.5 py-1.5 transition-colors ${
                active
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
              aria-label={tab.label}
              aria-current={active ? 'page' : undefined}
            >
              <div className="relative shrink-0">
                <Icon className="w-5 h-5" strokeWidth={active ? 2.25 : 1.75} />
                {badgeCount > 0 && (
                  <span
                    className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-4 text-center"
                    aria-label={`${badgeCount} unread`}
                  >
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </div>
              <span
                className={`mt-1 text-[10px] leading-none tracking-tight whitespace-nowrap max-w-full truncate ${
                  active ? 'font-semibold' : 'font-medium'
                }`}
              >
                {tab.label}
              </span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
